import { useEffect, useState } from 'react'
import api from '../services/api'

interface CompradorItem {
  cpr_cod: number
  cpr_des: string
  cpr_dmo: string | null
}

interface CompradorDetalhe {
  cpr_cod: number
  cpr_des: string
  cpr_dmo: string | null
}

const NOVO_COMPRADOR: CompradorDetalhe = {
  cpr_cod: 0,
  cpr_des: '',
  cpr_dmo: null,
}

function Campo({ label, width, children }: { label: string; width?: number | string; children: React.ReactNode }) {
  return (
    <div className="field-cad" style={{ width }}>
      <label style={{ display: 'block', minHeight: '1.1rem', lineHeight: 1.2 }}>{label}</label>
      {children}
    </div>
  )
}

function desativado(cpr_dmo: string | null) {
  return !!cpr_dmo && cpr_dmo.slice(0, 10) > '0001-01-01'
}

export default function Compradores() {
  const [lista, setLista] = useState<CompradorItem[]>([])
  const [busca, setBusca] = useState('')
  const [carregandoLista, setCarregandoLista] = useState(false)
  const [selecionado, setSelecionado] = useState<CompradorDetalhe | null>(null)
  const [carregandoDetalhe, setCarregandoDetalhe] = useState(false)
  const [form, setForm] = useState<CompradorDetalhe | null>(null)
  const [salvando, setSalvando] = useState(false)
  const [msgSalvar, setMsgSalvar] = useState<{ texto: string; ok: boolean } | null>(null)

  async function carregarLista(termo: string) {
    setCarregandoLista(true)
    try {
      const r = await api.get('/compradores', { params: termo ? { busca: termo } : {} })
      setLista(r.data)
    } finally {
      setCarregandoLista(false)
    }
  }

  useEffect(() => { carregarLista('') }, [])
  useEffect(() => {
    const t = setTimeout(() => carregarLista(busca), 350)
    return () => clearTimeout(t)
  }, [busca])

  async function selecionar(cod: number) {
    setCarregandoDetalhe(true)
    setMsgSalvar(null)
    try {
      const r = await api.get(`/compradores/${cod}`)
      setSelecionado(r.data)
      setForm({ ...r.data, cpr_dmo: r.data.cpr_dmo ? String(r.data.cpr_dmo).slice(0, 10) : '' })
    } finally {
      setCarregandoDetalhe(false)
    }
  }

  function novo() {
    setSelecionado(NOVO_COMPRADOR)
    setForm({ ...NOVO_COMPRADOR, cpr_dmo: '' })
    setMsgSalvar(null)
  }

  function setCampo<K extends keyof CompradorDetalhe>(campo: K, valor: CompradorDetalhe[K]) {
    setForm(f => f && { ...f, [campo]: valor })
  }

  async function salvar() {
    if (!form) return
    setSalvando(true)
    setMsgSalvar(null)
    try {
      if (form.cpr_cod) {
        await api.put(`/compradores/${form.cpr_cod}`, form)
        setMsgSalvar({ texto: 'Alterações salvas com sucesso.', ok: true })
        await selecionar(form.cpr_cod)
      } else {
        const r = await api.post('/compradores', form)
        setMsgSalvar({ texto: 'Comprador criado com sucesso.', ok: true })
        await selecionar(r.data.cpr_cod)
      }
      await carregarLista(busca)
    } catch (e: any) {
      setMsgSalvar({ texto: e?.response?.data?.error || 'Erro ao salvar.', ok: false })
    } finally {
      setSalvando(false)
    }
  }

  return (
    <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
      {/* Lista */}
      <div className="card" style={{ width: 320, flexShrink: 0 }}>
        <h3 style={{
          fontSize: '1rem', fontWeight: 700, marginBottom: '0.9rem', color: '#1a6ea8',
          paddingBottom: '0.6rem', borderBottom: '2px solid #eff6ff',
        }}>
          Compradores
        </h3>

        <div style={{ display: 'flex', gap: 8, marginBottom: '0.75rem', alignItems: 'center' }}>
          <div className="field" style={{ flex: 1 }}>
            <input
              type="text"
              placeholder="Buscar por código ou nome..."
              value={busca}
              onChange={e => setBusca(e.target.value)}
            />
          </div>
          <button className="btn btn-primary btn-sm" onClick={novo} title="Novo comprador">+ Novo</button>
        </div>

        {carregandoLista && <p style={{ color: '#6b7280', fontSize: '0.85rem' }}>Carregando...</p>}
        {!carregandoLista && lista.length === 0 && (
          <p style={{ color: '#6b7280', fontSize: '0.85rem' }}>Nenhum comprador encontrado.</p>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 560, overflowY: 'auto' }}>
          {lista.map(c => {
            const ativo = selecionado?.cpr_cod === c.cpr_cod
            const inativo = desativado(c.cpr_dmo)
            return (
              <button
                key={c.cpr_cod}
                onClick={() => selecionar(c.cpr_cod)}
                style={{
                  textAlign: 'left', border: '1px solid #e5e7eb', borderLeft: '4px solid #1a6ea8',
                  borderRadius: 6, padding: '8px 10px', cursor: 'pointer',
                  background: ativo ? '#eff6ff' : '#f8fafc',
                  borderColor: ativo ? '#1a6ea8' : '#e5e7eb',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontWeight: 700, fontSize: '0.82rem' }}>{c.cpr_cod}</span>
                  {inativo && (
                    <span style={{
                      fontSize: '0.68rem', fontWeight: 700, color: '#fff',
                      background: '#dc2626', borderRadius: 4, padding: '0.15rem 0.45rem',
                    }}>
                      Desativado
                    </span>
                  )}
                </div>
                <div style={{ fontSize: '0.8rem', color: '#374151', marginTop: 3 }}>{c.cpr_des}</div>
              </button>
            )
          })}
        </div>
      </div>

      {/* Detalhe */}
      <div className="card" style={{ flex: 1, minWidth: 0 }}>
        {!selecionado && !carregandoDetalhe && (
          <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>
            Selecione um comprador na lista ao lado, ou clique em "+ Novo".
          </p>
        )}

        {carregandoDetalhe && <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>Carregando detalhes...</p>}

        {selecionado && form && !carregandoDetalhe && (
          <>
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
              marginBottom: '0.6rem', paddingBottom: '0.5rem', borderBottom: '2px solid #eff6ff',
            }}>
              <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: '#1a6ea8' }}>
                {selecionado.cpr_cod ? `${selecionado.cpr_cod} — ${selecionado.cpr_des || '(sem nome)'}` : 'Novo Comprador'}
              </h3>
            </div>

            <div style={{
              background: '#f7faff', border: '1px solid #eef0f4', borderLeft: '4px solid #1a6ea8',
              borderRadius: '0.5rem', padding: '0.55rem 0.85rem', marginBottom: '0.55rem',
              display: 'flex', flexWrap: 'wrap', gap: '0.4rem 0.8rem', alignItems: 'flex-end',
            }}>
              <Campo label="Código" width={95}>
                <input type="text" value={selecionado.cpr_cod || '(novo)'} readOnly disabled style={{ width: '100%' }} />
              </Campo>
              <Campo label="Nome" width={280}>
                <input type="text" maxLength={25} value={form.cpr_des} onChange={e => setCampo('cpr_des', e.target.value)} style={{ width: '100%' }} />
              </Campo>
              <Campo label="Desativado" width={160}>
                <input type="date" value={form.cpr_dmo ?? ''} onChange={e => setCampo('cpr_dmo', e.target.value)} style={{ width: '100%' }} />
              </Campo>
              <span style={{ fontSize: '0.78rem', color: '#6b7280' }}>(data em branco = comprador ativo)</span>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <button className="btn btn-primary" onClick={salvar} disabled={salvando}>
                {salvando ? 'Salvando...' : 'Salvar alterações'}
              </button>
              {msgSalvar && (
                <span style={{
                  fontSize: '0.8rem', fontWeight: 600, padding: '0.35rem 0.8rem', borderRadius: 6,
                  background: msgSalvar.ok ? '#d1fae5' : '#fee2e2',
                  color: msgSalvar.ok ? '#065f46' : '#991b1b',
                }}>
                  {msgSalvar.texto}
                </span>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}
